import React from "react";
import { Box, Chip, Paper, Typography } from "@mui/material";
import moment from "moment";
import { useDispatch } from "react-redux";
import { i18n } from "../../../../translate/i18n";
import { TEquipmentWithServicesEquipmentService, TEquipmentWithServicesEquipmentServiceReplicate } from "../../../../models/iequipmentWithServices";
import * as actions from "../../../../store/actions";

type Props = {
  setStep: CallableFunction,
  equipmentService: TEquipmentWithServicesEquipmentService,
  equipmentServicesReplicate: TEquipmentWithServicesEquipmentServiceReplicate[],
}

type TChipColor = 'warning'|'error'|'success'

const EquipmentServiceCard: React.FC<Props> = (props) => {
  const dispatch = useDispatch();

  const lastService = props.equipmentServicesReplicate?.find((s: any) => s.equipmentServiceId === props.equipmentService._id);
  const today = moment(new Date());
  
  const days = lastService ? moment(lastService.expirationDate).startOf('day').diff(moment().startOf('day'), 'days') : null;

  let daysRemainingOrOverdueString = ''
  if (days) {
    const dayOrDaysRemaining = days === 1 ? i18n.t('selectServiceType.dayRemaining') : i18n.t('selectServiceType.daysRemaining');
    const dayOrDaysOverdue = days === -1 ? i18n.t('selectServiceType.dayOverdue') : i18n.t('selectServiceType.daysOverdue');
    daysRemainingOrOverdueString = days >= 0 ? ` (${days} ${dayOrDaysRemaining})` : ` (${days && -1 * days} ${dayOrDaysOverdue})`;
  }
  
  const getChipProps = () => {
		if (lastService) {
			if (!lastService.expirationDate) {
				return { text: i18n.t('selectServiceType.onTime'), color: 'primary'}
			} else {
				if (days && days >= 0 ) {
          return { text: i18n.t('selectServiceType.onTime'), color: 'primary'}
        }
        return { text: i18n.t('selectServiceType.overdue'), color: 'error'}
			}
		} else {
			return { text: i18n.t('selectServiceType.noService'), color: 'default'}
		}
	}
  const chipProps = getChipProps();

  const handleSelectEquipmentService = () => {
    dispatch(actions.createServiceSetServiceDraftId(''));
    dispatch(actions.createServiceSelectEquipmentService(props.equipmentService));
    props.setStep(2);
  }

  return(
    <Paper sx={[sxs.equipmentServiceCard, sxs.equipmentServiceCardHover]} onClick={handleSelectEquipmentService}>
      <Box>
        <Typography variant='subtitle1'>
          {`${props.equipmentService.serviceType.name} ${props.equipmentService.frequency.name}`}
        </Typography>
        {
          props.equipmentService.mandatory && <Typography variant='caption'>
          {i18n.t('selectServiceType.mandatory') as string}
          </Typography>
        }
        {
          lastService && <Box>
            <Typography sx={sxs.serviceDate} variant='caption'>
              {`${i18n.t('selectServiceType.lastPerform')} ${moment(lastService.date).format('DD/MM/YYYY')}`}
            </Typography>
            { lastService.expirationDate ?
              <Box>
                <Typography sx={sxs.serviceDate} variant='caption'>
                  {`${i18n.t('selectServiceType.expiration')} ${moment(lastService.expirationDate).format('DD/MM/YYYY')}`}
                </Typography>
                <Typography sx={sxs.serviceDate} variant='caption'>
                  {daysRemainingOrOverdueString}
                </Typography>
              </Box>
              :
              <Box>
                <Typography sx={sxs.serviceDate} variant='caption'>
                  {i18n.t('selectServiceType.onDemand') as string}
                </Typography>
              </Box>
            }
          </Box>
        }
      </Box>
      <Box sx={sxs.chipBox}>
        <Chip
          label={chipProps.text}
          color={chipProps.color as TChipColor}
          sx={sxs.serviceCardChip}
        />
      </Box>
    </Paper>
  )
}

const sxs = {
  equipmentServiceCard: {
    display: 'flex',
    padding: '12px 24px',
    flexDirection: 'row',
    justifyContent: 'space-between',
    gap: '36px',
    borderRadius: '12px',
    border: '1px solid var(--primary-hover, #ECEEF3)',
    boxShadow: '0px 2px 1px -1px rgba(0, 0, 0, 0.20), 0px 1px 1px 0px rgba(0, 0, 0, 0.14), 0px 1px 3px 0px rgba(0, 0, 0, 0.12)',
    marginBottom: '24px',
    height: '130px',
    minWidth: '480px',
    '@media (min-width: 1400px)': {
      width: '48%',
    }
  },
  equipmentServiceCardHover: {
    '&:hover': {
      backgroundColor: '#ECEEF3',
      boxShadow: '0px 5px 5px -3px rgba(0, 0, 0, 0.20), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
      cursor: 'pointer',
    }
  },
  serviceDate: {
    color: '#6F8189',
  },
  chipBox: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  serviceCardChip: {
    width: '100px',
    height: '24px',
    padding: 0,
  },
}

export default EquipmentServiceCard;